// IMPORTS
import React, { useState, useEffect, useCallback } from 'react';
import { Table, AutoComplete, Input, Modal, Form, Button, Tag, message, Space, Select } from 'antd';
import { Api_VariavelGlobal } from '../global';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const CategoriesList = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isNewModalVisible, setIsNewModalVisible] = useState(false);

  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [aplicacoesSelecionadas, setAplicacoesSelecionadas] = useState([]);

  const [newCategoria, setNewCategoria] = useState({
    nome: '',
    status: 1,
    pai: null,
    link: '',
    imagem: '',
    aplicacoes: []
  });

  const [listaAplicacoes, setListaAplicacoes] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Formata datas
  const formatarData = (dataISO) => {
    const data = new Date(dataISO);
    return data.toLocaleString('pt-BR');
  };

  // Buscar aplicações
  const fetchAplicacoes = async () => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/aplicacoes`);
      const apps = await res.json();
      setListaAplicacoes(apps);
    } catch (err) {
      console.error("Erro ao buscar aplicações:", err);
      message.error("Erro ao buscar aplicações.");
    }
  };

  // Buscar categorias
  const fetchCategorias = useCallback(async () => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/categorias`);
      const categorias = await res.json();

      const categoriasFormatadas = categorias.map(cat => ({
        key: cat.ID_CATEGORIA,
        nome: cat.NOME,
        status: cat.STATUS,
        pai: cat.PAI_ID,
        link: cat.LINK,
        imagem: cat.IMAGEM,
        aplicacoes: cat.Aplicacaos?.map(a => a.ID_APLICACAO) || [], // IDs para o Select
        aplicacoesNomes: cat.Aplicacaos?.map(a => a.NOME).join(", ") || '-',
        data: cat.DATA ? formatarData(cat.DATA) : ''
      }));

      setData(categoriasFormatadas);
      setFilteredData(categoriasFormatadas);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err);
      message.error("Erro ao buscar categorias.");
    }
  }, []);

  useEffect(() => {
    fetchCategorias();
    fetchAplicacoes();
  }, [fetchCategorias]);

  // Busca
  const handleSearch = (value) => {
    setSearchValue(value);
    const filtered = data.filter(cat =>
      cat.nome.toLowerCase().includes(value.toLowerCase()) ||
      (cat.link && cat.link.toLowerCase().includes(value.toLowerCase())) ||
      (cat.pai && cat.pai.toString().includes(value))
    );
    setFilteredData(filtered);
    setOptions(filtered.map(cat => ({ value: cat.nome, label: cat.nome })));
  };

  const onSelect = (value) => {
    setSearchValue(value);
    setFilteredData(data.filter(cat => cat.nome === value));
  };

  // Modal Editar
  const showEditModal = (categoria) => {
    setCategoriaSelecionada(categoria);
    setAplicacoesSelecionadas(categoria.aplicacoes || []); // seleciona aplicações da categoria
    setIsModalVisible(true);
  };

  const handleEditModalOk = async () => {
    setConfirmLoading(true);
    try {
      // Atualizar categoria
      await fetch(`${Api_VariavelGlobal}/api/categorias/${categoriaSelecionada.key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          NOME: categoriaSelecionada.nome,
          STATUS: categoriaSelecionada.status,
          PAI_ID: categoriaSelecionada.pai,
          LINK: categoriaSelecionada.link,
          IMAGEM: categoriaSelecionada.imagem
        })
      });

      // Atualizar aplicações da categoria
      await fetch(`${Api_VariavelGlobal}/api/categorias/${categoriaSelecionada.key}/aplicacoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aplicacoes: aplicacoesSelecionadas })
      });

      message.success("Categoria atualizada!");
      setIsModalVisible(false);
      fetchCategorias();
    } catch (err) {
      console.error(err);
      message.error("Erro ao atualizar categoria.");
    } finally {
      setConfirmLoading(false);
    }
  };

  // Modal Nova Categoria
  const handleNewModalOk = async () => {
  setConfirmLoading(true);
  try {
    const body = {
      NOME: newCategoria.nome,        // obrigatorio
      PAI_ID: newCategoria.pai || null,
      STATUS: newCategoria.status,
      LINK: newCategoria.link || null,
      IMAGEM: newCategoria.imagem || null
      // APLICACOES pode ser enviada separadamente se o back-end aceitar
    };

    const res = await fetch(`${Api_VariavelGlobal}/api/categorias`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || "Erro ao criar categoria");
    }

    message.success("Categoria criada com sucesso!");
    setIsNewModalVisible(false);
    setNewCategoria({ nome: '', status: 1, pai: null, link: '', imagem: '', aplicacoes: [] });
    fetchCategorias();
  } catch (err) {
    console.error(err);
    message.error(err.message);
  } finally {
    setConfirmLoading(false);
  }
};


  // Excluir categoria
  const confirmarExclusao = (categoria) => {
    Modal.confirm({
      title: "Excluir Categoria?",
      content: (
        <>
          Esta ação é <b style={{ color: 'red' }}>irreversível</b>.<br />
          Deseja excluir <b>{categoria.nome}</b>?
        </>
      ),
      okText: "Sim, excluir",
      okType: "danger",
      cancelText: "Cancelar",
      async onOk() {
        try {
          const res = await fetch(`${Api_VariavelGlobal}/api/categorias/${categoria.key}`, { method: "DELETE" });
          if (res.ok) {
            message.success("Categoria excluída!");
            fetchCategorias();
          } else {
            message.error("Erro ao excluir categoria.");
          }
        } catch (err) {
          console.error(err);
          message.error("Erro ao processar exclusão.");
        }
      }
    });
  };

  // Colunas da tabela
  const columns = [
    {
      title: "Imagem",
      dataIndex: "imagem",
      key: "imagem",
      render: img => {
        const safeName = typeof img === "string" && img.match(/^[a-zA-Z0-9._-]+$/) ? img : null;
        return safeName ? <img src={`/api/uploads_categorias/${safeName}`} alt="img" style={{ width: 40, borderRadius: 4 }} /> : "-";
      }
    },
    { title: "Nome", dataIndex: "nome", key: "nome" },
    { title: "Categoria Pai", dataIndex: "pai", key: "pai", render: pai => pai || "-" },
    { title: "Data", dataIndex: "data", key: "data" },
    { title: "Aplicações", dataIndex: "aplicacoesNomes", key: "aplicacoesNomes" },
    { title: "Status", dataIndex: "status", key: "status", render: s => <Tag color={s === 1 ? "blue" : "red"}>{s === 1 ? "Ativo" : "Inativo"}</Tag> },
    {
      title: "Ação",
      key: "acao",
      render: (_, categoria) => (
        <Space>
          <Button type="link" onClick={() => showEditModal(categoria)}>Editar</Button>
          <Button danger type="link" onClick={() => confirmarExclusao(categoria)}>Excluir</Button>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space style={{ marginBottom: 15 }}>
        <AutoComplete
          style={{ width: 300 }}
          popupMatchSelectWidth={252}
          value={searchValue}
          options={options}
          onSelect={onSelect}
          onSearch={handleSearch}
          size="large"
        >
          <Input.Search placeholder="Buscar categoria" enterButton size="large" />
        </AutoComplete>

        <Button type="primary" size="large" onClick={() => setIsNewModalVisible(true)}>Nova Categoria</Button>
        <Button type="default" size="large" onClick={() => navigate('/categoryimages')}>Imagens das Categorias</Button>
      </Space>

      <Table columns={columns} dataSource={filteredData} size="small" />

      {/* Modal Editar */}
      <Modal
        title="Editar Categoria"
        open={isModalVisible}
        onOk={handleEditModalOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={confirmLoading}
      >
        <Form layout="vertical">
          <Form.Item label="Nome">
            <Input value={categoriaSelecionada?.nome} onChange={e => setCategoriaSelecionada(prev => ({ ...prev, nome: e.target.value }))} />
          </Form.Item>

          <Form.Item label="Aplicações">
            <Select
              mode="multiple"
              allowClear
              value={aplicacoesSelecionadas}  // seleciona automaticamente as aplicações da categoria
              onChange={setAplicacoesSelecionadas}
            >
              {listaAplicacoes.map(app => (
                <Option key={app.ID_APLICACAO} value={app.ID_APLICACAO}>{app.NOME}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Link">
            <Input value={categoriaSelecionada?.link} onChange={e => setCategoriaSelecionada(prev => ({ ...prev, link: e.target.value }))} />
          </Form.Item>

          <Form.Item label="Categoria Pai">
            <Input value={categoriaSelecionada?.pai} onChange={e => setCategoriaSelecionada(prev => ({ ...prev, pai: e.target.value }))} />
          </Form.Item>

          <Form.Item label="Imagem (URL)">
            <Input value={categoriaSelecionada?.imagem} disabled />
          </Form.Item>

          <Form.Item label="Status">
            <Input value={categoriaSelecionada?.status} onChange={e => setCategoriaSelecionada(prev => ({ ...prev, status: Number(e.target.value) }))} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Nova Categoria */}
      <Modal
        title="Nova Categoria"
        open={isNewModalVisible}
        onOk={handleNewModalOk}
        onCancel={() => setIsNewModalVisible(false)}
        confirmLoading={confirmLoading}
      >
        <Form layout="vertical">
          <Form.Item label="Nome">
            <Input value={newCategoria.nome} onChange={e => setNewCategoria(prev => ({ ...prev, nome: e.target.value }))} />
          </Form.Item>

          <Form.Item label="Aplicações">
            <Select
              mode="multiple"
              allowClear
              value={newCategoria.aplicacoes}
              onChange={values => setNewCategoria(prev => ({ ...prev, aplicacoes: values }))}
            >
              {listaAplicacoes.map(app => (
                <Option key={app.ID_APLICACAO} value={app.ID_APLICACAO}>{app.NOME}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Link">
            <Input value={newCategoria.link} onChange={e => setNewCategoria(prev => ({ ...prev, link: e.target.value }))} />
          </Form.Item>

          <Form.Item label="Categoria Pai">
            <Input value={newCategoria.pai} onChange={e => setNewCategoria(prev => ({ ...prev, pai: e.target.value }))} />
          </Form.Item>

          <Form.Item label="Imagem (URL)">
            <Input value={newCategoria.imagem} disabled />
          </Form.Item>

          <Form.Item label="Status">
            <Input value={newCategoria.status} onChange={e => setNewCategoria(prev => ({ ...prev, status: Number(e.target.value) }))} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CategoriesList;
